import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type { PublishDrawingChoice } from '../../hooks/useDrawingPersistence';

/**
 * #1060 (ADR 0019 D13, O1): making a drawing public when it shows molecules only the user's space
 * holds. The picture discloses the structures either way; publishing the molecules too (their
 * Monographs and search cards) is a separate, explicit choice -- off unless ticked.
 */
export function PublishDrawingDialog({
  privateMolecules,
  onAnswer
}: {
  readonly privateMolecules: number;
  readonly onAnswer: (choice: PublishDrawingChoice) => void;
}) {
  const { t } = useTranslation();
  const [alsoMolecules, setAlsoMolecules] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(true, dialogRef);
  useEscapeKey(true, () => onAnswer('cancel'));
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    confirmRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  return (
    <div className="export-dialog-backdrop">
      <div
        ref={dialogRef}
        className="save-drawing-dialog publish-drawing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-drawing-title"
        data-testid="publish-drawing-dialog"
      >
        <h2 id="publish-drawing-title">{t('publishDrawing.heading')}</h2>
        <p>{t('publishDrawing.structuresVisible')}</p>
        <p>{t('publishDrawing.privateMolecules', { count: privateMolecules })}</p>
        <label className="publish-drawing-molecules">
          <input type="checkbox" checked={alsoMolecules} onChange={(event) => setAlsoMolecules(event.target.checked)} />
          <span>{t('publishDrawing.alsoMolecules', { count: privateMolecules })}</span>
        </label>
        <div className="persistence-actions">
          <button type="button" onClick={() => onAnswer('cancel')}>
            {t('exportDialog.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => onAnswer(alsoMolecules ? 'drawingAndMolecules' : 'drawing')}
          >
            {t('publishDrawing.makePublic')}
          </button>
        </div>
      </div>
    </div>
  );
}
